import { Link } from 'react-router-dom';
import { useSiteSettings } from '@shared/hooks/useSiteSettings';

/**
 * 100% PARITY VERSION: Replicating Angular BenavidezBoxing-footer exactly
 */
export function Footer() {
    const { data: settings } = useSiteSettings();
    const currentYear = new Date().getFullYear();

    const footerLinks = settings?.footerLinks || {
        shopOnline: [
            { label: 'Fitness Products', link: '/materials' },
            { label: 'Delivery & Returns', link: '/shipping' },
            { label: 'Exchange & Refunds', link: '/returns' },
            { label: 'Order Tracking', link: '/tracking' },
            { label: 'FAQs', link: '/faq' }
        ],
        information: [
            { label: 'How To Buy Fitness', link: '/how-to-buy' },
            { label: 'Terms & Conditions', link: '/terms' },
            { label: 'Privacy Policy', link: '/privacy' },
            { label: 'Shipping Policy', link: '/shipping-policy' },
            { label: 'Disclaimer', link: '/disclaimer' }
        ]
    };

    const contactEmail = settings?.contactEmail || 'hello@BenavidezBoxing.shop';
    const contactPhone = settings?.contactPhone || '+92 ...';
    const address = settings?.address || 'Online Support';

    const getLogoUrl = (path: string) => {
        if (!path) return '';
        if (path.startsWith('http')) return path;
        return `${import.meta.env.VITE_API_BASE_URL.replace(/(\/api)(?=\/api$|$)/, '')}${path}`;
    };

    return (
        <footer className="bg-white text-gray-800">
            {/* Main Footer Content */}
            <div className="py-16 md:py-20 lg:pb-12 border-b border-gray-50" style={{
                marginLeft: '24px'
            }}>
                <div className="mx-auto px-4 md:px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-y-12 gap-x-12 items-start">

                        {/* Brand Column */}
                        <div className="lg:pr-8">
                            <div className="mb-6">
                                {settings?.logoUrl ? (
                                    <img
                                        src={getLogoUrl(settings.logoUrl)}
                                        alt="Logo"
                                        className="w-auto object-contain mb-4"
                                        style={{ maxHeight: '150px' }}
                                    />
                                ) : (
                                    <h2 className="text-3xl font-bold tracking-tight text-gray-900">
                                        BenavidezBoxing
                                    </h2>
                                )}
                            </div>
                            <p className="text-gray-600 mb-8 leading-relaxed text-[15px]">
                                {settings?.aboutText || 'Lorem ipsum dolor sit amet, conse elit, sedid do eiusmod tempor incidi ut labore et dolore.'}
                            </p>

                            {/* Contact & Social row */}
                            <div className="flex flex-wrap items-center gap-4">
                                <Link to="/contact-us"
                                    className="inline-block bg-yellow-500 hover:bg-yellow-600 text-white font-semibold px-6 py-2.5 rounded-md transition-all duration-300 shadow-sm text-sm">
                                    Contact Us
                                </Link>

                                <div className="flex gap-2">
                                    {(settings?.socialLinks || []).map((social: any, i: number) => (
                                        <a href={social.url} key={i} target="_blank" rel="noopener noreferrer"
                                            className="w-9 h-9 bg-gray-100 rounded-full flex items-center justify-center hover:bg-[#D4AF37] hover:text-white transition-all duration-300 text-gray-600">
                                            <i className={`${social.icon} text-lg`}></i>
                                        </a>
                                    ))}
                                    {(!settings?.socialLinks || settings.socialLinks.length === 0) && (
                                        <div className="flex gap-2">
                                            {/* Fallback to static icons if no social links in CMS */}
                                            <a href="#" target="_blank" rel="noopener noreferrer"
                                                className="w-9 h-9 bg-gray-100 rounded-full flex items-center justify-center hover:bg-[#D4AF37] hover:text-white transition-all duration-300 text-gray-600">
                                                <i className="ri-facebook-fill text-lg"></i>
                                            </a>
                                            <a href="#" target="_blank" rel="noopener noreferrer"
                                                className="w-9 h-9 bg-gray-100 rounded-full flex items-center justify-center hover:bg-[#D4AF37] hover:text-white transition-all duration-300 text-gray-600">
                                                <i className="ri-instagram-line text-lg"></i>
                                            </a>
                                            <a href="#" target="_blank" rel="noopener noreferrer"
                                                className="w-9 h-9 bg-gray-100 rounded-full flex items-center justify-center hover:bg-[#D4AF37] hover:text-white transition-all duration-300 text-gray-600">
                                                <i className="ri-linkedin-fill text-lg"></i>
                                            </a>
                                            <a href="#" target="_blank" rel="noopener noreferrer"
                                                className="w-9 h-9 bg-gray-100 rounded-full flex items-center justify-center hover:bg-[#D4AF37] hover:text-white transition-all duration-300 text-gray-600">
                                                <i className="ri-tiktok-fill text-lg"></i>
                                            </a>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Shop Online Column */}
                        <div>
                            <h4 className="text-xl font-bold mb-6 text-gray-900">
                                Shop Online</h4>
                            <ul className="space-y-4">
                                {(footerLinks.shopOnline || []).map((link: any) => (
                                    <li key={link.label}>
                                        <Link to={link.href || link.link}
                                            className="text-gray-600 hover:text-[#D4AF37] transition-colors text-[15px]">
                                            {link.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Information Column */}
                        <div>
                            <h4 className="text-xl font-bold mb-6 text-gray-900">
                                Information</h4>
                            <ul className="space-y-4">
                                {(footerLinks.information || []).map((link: any) => (
                                    <li key={link.label}>
                                        <Link to={link.href || link.link}
                                            className="text-gray-600 hover:text-[#D4AF37] transition-colors text-[15px]">
                                            {link.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Contact Info Column */}
                        <div className="lg:col-span-2">
                            <h4 className="text-xl font-bold mb-6 text-gray-900 leading-tight">
                                Contact Information
                            </h4>
                            <div className="space-y-5 text-[15px] text-gray-600 leading-relaxed">
                                <p><span className="font-bold text-gray-900">Address:</span> {address}</p>
                                <p><span className="font-bold text-gray-900">Tel:</span> {contactPhone}</p>
                                <p><span className="font-bold text-gray-900">Email:</span> <a
                                    href={`mailto:${contactEmail}`} className="hover:text-[#D4AF37]">{
                                        contactEmail}</a></p>
                            </div>
                        </div>

                    </div>
                </div>
            </div>

            {/* Copyright Bar */}
            <div className="py-8 text-center text-gray-500 text-sm">
                <p>
                    {settings?.footerCopyright || `Copyright ${currentYear} BenavidezBoxing. All rights reserved.`}
                </p>
            </div>
        </footer>
    );
}

export default Footer;
